<script setup>
import {useI18n} from "vue-i18n";
import Input from "@/components/forms/Input.vue";
import {computed} from "vue";
import Select from "@/components/forms/Select.vue";
import Multiselect from "@vueform/multiselect";

const props = defineProps({
  modelValue: Object,
  investmentQuestionnaires: Array,
  amlQuestionnaires: Array,
  forms: Array,
  meetingRecords: Array,
  contractTypes: Array,
  documentsTemplates: Array,
  strategies: Array,
})

const {t} = useI18n();

const emit = defineEmits(['update:modelValue'])
const update = (key, value) => {
  emit('update:modelValue', { ...props.modelValue, [key]: value })
}

const selectedContractTypeName = computed(() => {
  const contractType = props.contractTypes.find(type => type.id === props.modelValue.contract_type) || null;
  return contractType ? contractType.name : '';
})

// Trigger platebních podmínek lze nastavit pouze na položku, která je vybrána
// v jiném inputu tohoto nastavení — dokument, formulář, dotazník nebo záznam z jednání.
// Všechna ID normalizujeme na Number, protože HTML select ukládá hodnoty jako string
// (přes $event.target.value), ale API vrací číselná ID.
const triggerableTemplates = computed(() => {
  const toNumbers = arr => (arr ?? []).map(Number)

  const selectedDocIds        = toNumbers([...(props.modelValue.documents_templates_ids ?? []), ...(props.modelValue.documents_templates_w8_ids ?? [])])
  const selectedFormIds       = toNumbers(props.modelValue.forms)
  const selectedMeetingIds    = toNumbers(props.modelValue.meeting_records)
  const selectedInvestmentIds = toNumbers(props.modelValue.investment_questionnaires)
  const selectedAmlIds        = toNumbers(props.modelValue.aml_questionnaires)

  return [
    ...props.documentsTemplates.filter(t => selectedDocIds.includes(Number(t.id))),
    ...(props.forms ?? []).filter(f => selectedFormIds.includes(Number(f.id))),
    ...(props.meetingRecords ?? []).filter(mr => selectedMeetingIds.includes(Number(mr.id))),
    ...(props.investmentQuestionnaires ?? []).filter(q => selectedInvestmentIds.includes(Number(q.id))),
    ...(props.amlQuestionnaires ?? []).filter(q => selectedAmlIds.includes(Number(q.id))),
  ]
})

// Mapování na formát { value, label } pro Multiselect
const formsOptions = computed(() =>
    (props.forms ?? []).map(f => ({ value: f.id, label: f.name }))
)
const documentsTemplatesOptions = computed(() =>
    (props.documentsTemplates ?? []).map(d => ({ value: d.id, label: d.name }))
)
// Strategie jsou ze servisu již ve správném formátu { value, label }
const strategiesOptions = computed(() => props.strategies ?? [])

</script>

<template>
  <div class="border-t pt-4">
    <!-- Investiční dotazník -->
    <div class="mb-3">
      <label class="form-label">Investiční dotazník</label>
      <select class="form-select" :value="modelValue.investment_questionnaires?.[0]" @change="update('investment_questionnaires', [$event.target.value])">
        <option value="">---</option>
        <option v-for="(q, i) in investmentQuestionnaires" :key="i" :value="q.id">
          {{ q.name }}
        </option>
      </select>
    </div>

    <!-- AML dotazník -->
    <div class="mb-3">
      <label class="form-label">AML dotazník</label>
      <select class="form-select" :value="modelValue.aml_questionnaires?.[0]" @change="update('aml_questionnaires', [$event.target.value])">
        <option value="">---</option>
        <option v-for="(q, i) in amlQuestionnaires" :key="i" :value="q.id">
          {{ q.name }}
        </option>
      </select>
    </div>

    <!-- Formuláře -->
    <div class="mb-3">
      <label class="form-label">Formuláře</label>
      <Multiselect
          :model-value="modelValue.forms"
          @update:model-value="update('forms', $event)"
          :options="formsOptions"
          :close-on-select="false"
          :hide-selected="false"
          placeholder="Vyberte formuláře"
          mode="tags"
      />
    </div>

    <!-- Strategie -->
    <div class="mb-3">
      <label class="form-label">Strategie</label>
      <Multiselect
          :model-value="modelValue.strategies"
          @update:model-value="update('strategies', $event)"
          :options="strategiesOptions"
          :close-on-select="false"
          :hide-selected="false"
          placeholder="Vyberte strategie"
          mode="tags"
      />
    </div>

    <!-- Záznam z jednání -->
    <div class="mb-3">
      <label class="form-label">Záznam z jednání</label>
      <select class="form-select" :value="modelValue.meeting_records?.[0]" @change="update('meeting_records', [$event.target.value])">
        <option value="">---</option>
        <option v-for="(q, i) in meetingRecords" :key="i" :value="q.id">
          {{ q.name }}
        </option>
      </select>
    </div>

    <!-- Přepínače -->
    <div class="row g-3">
      <div v-for="key in ['identification','client_complex_form','address','bank_account','documents','calculation','additional_documents_enabled']" :key="key" class="form-group">
        <div class="form-check form-switch">
          <input class="form-check-input" type="checkbox" :id="key" :checked="modelValue[key]" @change="update(key, $event.target.checked)" />
          <label class="form-check-label" :for="key">{{ t('settings.onboardingOptions.'+ key) }}</label>
        </div>
        <div v-if="key === 'calculation' && modelValue.calculation && modelValue.contract_type" class="row mb-3">
          <span>Klientovi budou strategie přiřazeny k vytvořené smlouvě: {{ selectedContractTypeName }}</span>
        </div>
        <div v-if="key === 'calculation' && !modelValue.contract_type && modelValue.calculation" class="row mb-3">
          <span>Je potřeba zvolit vytvoření smlouvy a její typ.</span>
        </div>
      </div>
    </div>

    <!-- Typ smlouvy -->
    <div class="mt-3">
      <label class="form-label">Typ smlouvy</label>
      <select class="form-select" :value="modelValue.contract_type" @change="update('contract_type', Number($event.target.value))">
        <option value="">---</option>
        <option v-for="(contract, i) in contractTypes" :key="i" :value="contract.id">
          {{ contract.name }}
        </option>
      </select>
    </div>

    <!-- Šablony dokumentů -->
    <div class="row mb-3">
      <div class="col-12 form-group">
        <label class="form-label">Generovat dokumenty (W8)</label>
        <Multiselect
            :model-value="modelValue.documents_templates_w8_ids"
            @update:model-value="update('documents_templates_w8_ids', $event)"
            :options="documentsTemplatesOptions"
            :close-on-select="false"
            :hide-selected="false"
            placeholder="Vyberte šablony dokumentů (W8)"
            mode="tags"
        />
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-12 form-group">
        <label class="form-label">Generovat dokumenty (ostatní)</label>
        <Multiselect
            :model-value="modelValue.documents_templates_ids"
            @update:model-value="update('documents_templates_ids', $event)"
            :options="documentsTemplatesOptions"
            :close-on-select="false"
            :hide-selected="false"
            placeholder="Vyberte šablony dokumentů (ostatní)"
            mode="tags"
        />
      </div>
    </div>

    <!-- Trigger pro odeslání platebních podmínek — jeden dokument per nastavení -->
    <div class="row mb-3">
      <div class="col-12 form-group">
        <label class="form-label">Trigger platebních podmínek (při podpisu)</label>
        <select class="form-select"
                :value="modelValue.payment_terms_trigger_template_id"
                @change="update('payment_terms_trigger_template_id', $event.target.value ? Number($event.target.value) : null)">
          <option value="">---</option>
          <option v-for="(documentTemplate, index) in triggerableTemplates" :key="index" :value="documentTemplate.id">{{ documentTemplate.name }}</option>
        </select>
      </div>
    </div>
  </div>
</template>